import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useHistory } from "react-router-dom";
import CTA from "../components/CTA";
import WinesRow from "../images/wines-in-a-row.png";
import Sangria from "../images/sangria.png";
import SheWolf from "../images/corner/corner-shewolf.webp";
import GoodKarma from "../images/good-karma.png";
import Button from "../components/Button";
import { Link } from "react-scroll";
import { renderStrapiContent } from "../utils/contentfulOptions";
import moment from "moment";
import StrapiImage from "../components/StrapiImage";
import FullPageLoader from "../components/FullPageLoader";
import ReservationFinder from "../components/ReservationFinder";

const getPlainText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace(/\s+/g, " ").trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => getPlainText(item))
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (typeof value === "object") {
    return [value.text, getPlainText(value.children)]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
};

const excerpt = (text, max = 140) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "…";
};

const hasStrapiContent = (value) => {
  if (value == null || value === false) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) {
    return value.some((block) => {
      if (!block) return false;
      if (block.type === "image" && (block.image || block.url)) return true;
      return getPlainText(block).length > 0;
    });
  }
  if (typeof value === "object") {
    return Boolean(value.Title || value.title || value.documentId || value.id);
  }
  return false;
};

const getEventTitle = (event, index) => {
  const content = event?.content;
  if (Array.isArray(content)) {
    const heading = content.find(
      (block) => block?.type === "heading" && getPlainText(block)
    );
    if (heading) return getPlainText(heading);
    const paragraph = content.find(
      (block) => block?.type === "paragraph" && getPlainText(block)
    );
    if (paragraph) return getPlainText(paragraph);
  }
  return `Event ${index + 1}`;
};

const getEventAnchor = (event, index) =>
  `happening-event-${event?.id || index}`;

const WhatsHappening = (props) => {
  useEffect(() => {
    document.title = "Auburn Road - What's Happening";
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, []);

  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightbox]);

  const history = useHistory();
  if (!props.info || !props.music) {
    return <FullPageLoader />;
  }
  const { info } = props;
  const flattenAndSortByDates = (entries) => {
    const flattened = [];

    entries.forEach((entry) => {
      const dates = entry.s_date || [];
      dates.forEach((dateStr) => {
        const m = moment(dateStr.date);
        if (m.isValid()) {
          flattened.push({
            entry,
            date: m,
          });
        }
      });
    });
    const TODAY = moment().subtract(2, "days");
    const TWO_MONTHS = moment().add(2, "months");
    return flattened
      .sort((a, b) => a.date - b.date)
      .filter(
        (x) =>
          moment(x.date).isAfter(TODAY) && moment(x.date).isBefore(TWO_MONTHS)
      );
  };

  const sortedDates = flattenAndSortByDates(props.music);
  const renderPerformances =
    sortedDates.length === 0 ? (
      <p className="happening-shows__empty">No upcoming performances yet.</p>
    ) : (
      <ul className="happening-shows__list">
        {sortedDates.map((item) => {
          const { date, entry } = item;
          const name = entry.name;
          const location = entry.location;
          const website = entry.website;
          const dateKey = date.format("YYYY-MM-DD");
          return (
            <li className="happening-show" key={`${name}-${dateKey}`}>
              <time className="happening-show__date" dateTime={dateKey}>
                <span className="happening-show__weekday">
                  {date.format("ddd")}
                </span>
                <span className="happening-show__day">
                  {date.format("MMM D")}
                </span>
              </time>
              <div className="happening-show__details">
                <p className="happening-show__name">
                  {website ? (
                    <a
                      className="outgoing-link"
                      href={website.split(",")[0].trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {name}
                    </a>
                  ) : (
                    name
                  )}
                </p>
                {location ? (
                  <p className="happening-show__location">from {location}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    );

  const renderPlaylist = (x) => {
    return { __html: x };
  };

  const wineStory = info.wine_story;
  const storyTitle = wineStory?.Title || wineStory?.title || "";
  const storySlug = storyTitle.split(" ").join("-").toLowerCase();
  const storyPath =
    "/" + (wineStory?.unique_url || storySlug).replace(/^\//, "");
  const storyImage = StrapiImage(wineStory?.story_image) || GoodKarma;
  const storyBlurb = (() => {
    if (!wineStory) return "";
    const blurb = getPlainText(wineStory.blurb);
    if (blurb && blurb !== storyTitle) return excerpt(blurb);
    const content = getPlainText(wineStory.content);
    if (content && content !== storyTitle) return excerpt(content);
    return blurb;
  })();
  const playlists = info.playlist_week || info.playlist_of_the_week;
  const events = Array.isArray(info.events) ? info.events : [];
  const getEventImages = (event) => {
    const images = event?.image;
    const list = Array.isArray(images) ? images : images ? [images] : [];
    return list
      .map((img) => ({
        url: StrapiImage(img),
        alt: img?.alternativeText || img?.name || "",
      }))
      .filter((img) => img.url);
  };
  const eventHasContent = (event) =>
    Boolean(event) &&
    (getEventImages(event).length > 0 || hasStrapiContent(event.content));
  const visibleEvents = events.filter(eventHasContent);
  const hasEvents = visibleEvents.length > 0;
  const hasWineStory = hasStrapiContent(wineStory);
  const hasNewReleases = hasStrapiContent(info.new_releases);
  const hasExcitingNews = hasStrapiContent(info.exciting_news);
  const hasNiceThings = hasStrapiContent(info.nice_things);
  const hasPlaylist = hasStrapiContent(playlists);
  const hasOtherStuff = hasStrapiContent(info.other_stuff);

  const jumpLinks = [
    { id: "live", label: "Performances", show: true },
    { id: "events", label: "Events", show: hasEvents },
    { id: "wine-story", label: "Wine Story", show: hasWineStory },
    { id: "new-releases", label: "New Releases", show: hasNewReleases },
    { id: "exciting-news", label: "Exciting News", show: hasExcitingNews },
    { id: "nice-things", label: "Nice Things", show: hasNiceThings },
    { id: "playlist", label: "Playlist", show: hasPlaylist },
    { id: "other-stuff", label: "Other Stuff", show: hasOtherStuff },
  ].filter((link) => link.show);

  return (
    <>
      <img className="corner-shewolf" src={SheWolf} alt="" />
      <div className="container happening-page p-t-100">
        <section className="happening-hero">
          <h1 className="huge-text-no-quote mb-3">What's Happening</h1>
          <p className="happening-hero__intro">
            We are beyond excited to welcome you to Auburn Road. When you
            arrive, follow the signs to where our host will greet you and guide
            you to your table. Then sit back, relax, and enjoy. You have
            certainly earned it.
          </p>
        </section>

        <nav className="happening-jump" aria-label="On this page">
          {jumpLinks.map(({ id, label }) => (
            <Link
              className="happening-jump__link"
              activeClass="active"
              to={id}
              spy={true}
              smooth={true}
              offset={-50}
              duration={500}
              key={id}
            >
              {label}
            </Link>
          ))}
        </nav>

        <section
          className="happening-block happening-block--live"
          id="live"
          name="live"
        >
          <div className="happening-block__heading">
            <h2 className="huge-text-no-quote">Live from the vineyard stage</h2>
          </div>
          <p className="happening-block__intro">
            The Vineyard Stage at the Enoteca offers great music and
            performances all year long. Gourmet woodfire pizza available.
            Reservations recommended.
          </p>
          <p>
            <span className="fw-bold">LIVE! From the Vineyard Stage: </span>
            Every Saturday night, we welcome amazing performers from the region,
            Philly, and beyond.
          </p>
          <div className="happening-live">
            <img className="happening-photo" src={Sangria} alt="sangria art" />
            <div className="happening-shows" id="upcoming" name="upcoming">
              <h3 className="happening-shows__title">Upcoming performances</h3>
              {renderPerformances}
            </div>
          </div>
        </section>
        <ReservationFinder className="m-b-20" />
        {hasEvents && (
          <section className="happening-block" id="events" name="events">
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote mb-5">Events</h2>
              <label className="happening-event-nav">
                <span className="happening-event-nav__label">Jump to</span>
                <select
                  defaultValue=""
                  onChange={(event) => {
                    const target = document.getElementById(event.target.value);
                    if (!target) return;
                    const prefersReduced = window.matchMedia(
                      "(prefers-reduced-motion: reduce)"
                    ).matches;
                    target.scrollIntoView({
                      behavior: prefersReduced ? "auto" : "smooth",
                      block: "start",
                    });
                  }}
                >
                  <option value="" disabled>
                    Choose an event
                  </option>
                  {visibleEvents.map((event, eventIndex) => (
                    <option
                      key={event.id || eventIndex}
                      value={getEventAnchor(event, eventIndex)}
                    >
                      {getEventTitle(event, eventIndex)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {visibleEvents.map((event, i) => {
              const images = getEventImages(event);
              return (
                <article
                  className={`happening-event${
                    images.length ? "" : " happening-event--text-only"
                  }`}
                  id={getEventAnchor(event, i)}
                  key={event.id || i}
                >
                  {images.length > 0 && (
                    <div
                      className={`happening-event__media${
                        images.length > 1
                          ? " happening-event__media--stack"
                          : ""
                      }`}
                    >
                      {images.map((img, imgIndex) => (
                        <button
                          className="happening-event__image-btn"
                          type="button"
                          onClick={() => setLightbox(img)}
                          key={img.url + imgIndex}
                        >
                          <img
                            className="happening-photo"
                            src={img.url}
                            alt={img.alt}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  {hasStrapiContent(event.content) && (
                    <div className="happening-event__content happening-prose">
                      {renderStrapiContent(event.content)}
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        )}
      </div>
      <CTA
        className="p-t-25 align-items-center p-b-50 flex-column flex-md-row"
        light
        blurb={
          <div className="m-l-30 m-t-20 m-md-t-0">
            <p>Click here to find the perfect wine (or wines) for you!</p>
            <Button
              onClick={() =>
                (window.location.href =
                  "https://auburnroadvineyards.orderport.net/wines/Available-Wines")
              }
              type="secondary"
              external
            >
              check it out
            </Button>
          </div>
        }
      >
        <div>
          <img
            className="m-auto width-350"
            src={WinesRow}
            alt="wines in a row"
          />
        </div>
      </CTA>
      <div className="container happening-page">
        {hasWineStory && (
          <section
            className="happening-block"
            id="wine-story"
            name="wine-story"
          >
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">Wine Story</h2>
            </div>
            <article className="wine-story-wrap story-card story-card--featured">
              <div
                className="story-image pointer"
                style={{ "--bg-image": `url(${storyImage})` }}
                onClick={() => history.push(storyPath)}
                role="presentation"
              />
              <div className="story-card__copy">
                <span className="story-card__tag">This week's story</span>
                <h3
                  className="story-card__title pointer"
                  onClick={() => history.push(storyPath)}
                >
                  {storyTitle}
                </h3>
                {storyBlurb ? (
                  <p className="story-card__blurb blurb">{storyBlurb}</p>
                ) : null}
                <Button
                  onClick={() => history.push(storyPath)}
                  className="continue-reading"
                  small
                  type="plain"
                >
                  Continue reading
                </Button>
              </div>
            </article>
          </section>
        )}
        {hasNewReleases && (
          <section
            className="happening-block"
            id="new-releases"
            name="new-releases"
          >
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">New releases</h2>
            </div>
            <div className="happening-prose">
              {renderStrapiContent(info.new_releases)}
            </div>
          </section>
        )}
        {hasExcitingNews && (
          <section
            className="happening-block"
            id="exciting-news"
            name="exciting-news"
          >
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">Exciting News</h2>
            </div>
            <div className="happening-prose">
              {renderStrapiContent(info.exciting_news)}
            </div>
          </section>
        )}
        {hasNiceThings && (
          <section
            className="happening-block"
            id="nice-things"
            name="nice-things"
          >
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">Nice things people say</h2>
            </div>
            <div className="happening-prose">
              {renderStrapiContent(info.nice_things)}
            </div>
          </section>
        )}
        {hasPlaylist && (
          <section className="happening-block" id="playlist" name="playlist">
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">Playlist of the week</h2>
            </div>
            {(Array.isArray(playlists) ? playlists : [playlists]).map(
              (x, i) => (
                <div
                  className="happening-playlist"
                  dangerouslySetInnerHTML={renderPlaylist(x)}
                  key={i}
                ></div>
              )
            )}
          </section>
        )}
        {hasOtherStuff && (
          <section
            className="happening-block"
            id="other-stuff"
            name="other-stuff"
          >
            <div className="happening-block__heading">
              <h2 className="huge-text-no-quote">Other Stuff</h2>
            </div>
            <div className="happening-prose">
              {renderStrapiContent(info.other_stuff)}
            </div>
          </section>
        )}
      </div>
      {lightbox ? (
        <div
          className="happening-lightbox"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt || "Full size image"}
        >
          <button
            className="happening-lightbox__close"
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close full size image"
          />
          <img
            src={lightbox.url}
            alt={lightbox.alt}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </>
  );
};
export default withRouter(WhatsHappening);
